import React from 'react';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  Choice,
  INITIAL_CHOICE,
  ProductInfo,
  ProductLineSubRate,
  ProductLineToCarrier,
  RequiredSubInfoPresenter,
  SubscriberPresenter,
  SubscriberProductLineType,
} from 'types/electionService';
import {
  getPLineProductType,
  convertPlansToOptions,
  getChoiceByProductLineId,
  getDependentOnProductLineType,
} from 'global/utils/election/productLineElection';
import { checkProductLineRateValidity, getFormIdForProductLine } from 'global/utils/election/productLineValidation';
import SubscriberPLineCard from './SubscriberPLineCard';
import { SubscriberProductLinesStyled } from './styles';
import { CallbacksMap } from '../types';

interface Props {
  subscriber: SubscriberPresenter;
  products: ProductInfo[];
  productLineRates: ProductLineSubRate[];
  callbacksMap: CallbacksMap;
  productLineToCarrier: ProductLineToCarrier;
  isEditable: boolean;
  requiredSubInfo?: RequiredSubInfoPresenter;
}

interface ProductLineChoiceInfo {
  currentChoice: Choice;
  productLineType: SubscriberProductLineType;
  isValid: boolean;
  totalPremiumRate: string;
  showRateButton: boolean;
}

const SubscriberPLCardList: React.FC<Props> = (props) => {
  const {
    products,
    productLineRates,
    productLineToCarrier,
    subscriber: { id: subId, employeeClass, common, custom, choice },
    isEditable,
    callbacksMap,
    requiredSubInfo,
  } = props;

  const getProductLineChoiceInfo = (productLineId: ProductLineId): ProductLineChoiceInfo => {
    const currentChoice = getChoiceByProductLineId(choice, productLineId) || {
      ...INITIAL_CHOICE,
      productLineId,
    };
    const carrierId = productLineToCarrier[productLineId] || '';
    const productLineType = getPLineProductType(currentChoice.productId);

    const dependentOnProductLineType = getDependentOnProductLineType(productLineId, choice, requiredSubInfo);

    const rateCheckResult = checkProductLineRateValidity(
      common,
      {
        productLineId,
        employeeClass,
        carrierId,
        productId: currentChoice.productId,
        productLineType,
        dependentOnProductLineType,
      },
      requiredSubInfo,
    );
    const { errorMessageTypes, showActionButton } = rateCheckResult;

    const productLineRate = productLineRates.find((rate) => rate.productLineId === productLineId)?.rate || '0.00';

    const isOnlyMissingAnswersError =
      errorMessageTypes.length === 1 && errorMessageTypes[0] === 'MISSING_PRODUCT_LINE_ANSWERS';

    const totalPremiumRate = errorMessageTypes.length === 0 || isOnlyMissingAnswersError ? productLineRate : '0.00';
    const showRateButton = isEditable && showActionButton && !!callbacksMap?.onPLineRateChange;

    return {
      currentChoice,
      productLineType,
      isValid: errorMessageTypes.length === 0,
      totalPremiumRate,
      showRateButton,
    };
  };

  return (
    <SubscriberProductLinesStyled>
      {products.map(({ productLineId, plans }) => {
        const { currentChoice, productLineType, isValid, totalPremiumRate, showRateButton } =
          getProductLineChoiceInfo(productLineId);

        return (
          <SubscriberPLineCard
            key={productLineId}
            subscriberId={subId}
            productLineType={productLineType}
            isValid={isValid}
            dependents={custom.dependents}
            isEditable={isEditable}
            planOptions={convertPlansToOptions(plans, employeeClass)}
            currentChoice={currentChoice}
            totalPremiumRate={totalPremiumRate}
            formId={getFormIdForProductLine(productLineId, productLineType, requiredSubInfo)}
            showRateButton={showRateButton}
            updateDependentsChoice={callbacksMap?.updateDependentsChoice}
            updateProduct={callbacksMap?.updateProduct}
            onPLineRateChange={callbacksMap?.onPLineRateChange}
            onShowDetails={callbacksMap?.onShowDetails}
          />
        );
      })}
    </SubscriberProductLinesStyled>
  );
};

export default SubscriberPLCardList;
